import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveProfile } from "@/hooks/useActiveProfile";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { LogOut, User, Star, GraduationCap, Users, Briefcase, Shield, RefreshCw, ArrowLeft } from "lucide-react";

const roleIcons: Record<string, any> = {
  actor: Star,
  parent: User,
  coach: GraduationCap,
  agent: Users,
  manager: Users,
  vendor: Briefcase,
  casting_professional: Users,
  casting_associate: Users,
  producer: Users,
  studio: Users,
  admin: Shield,
};

const roleLabels: Record<string, string> = {
  actor: "Actor",
  parent: "Parent",
  coach: "Coach",
  agent: "Agent",
  manager: "Manager",
  vendor: "Industry Professional",
  casting_professional: "Casting Director",
  casting_associate: "Casting Associate",
  producer: "Producer",
  studio: "Studio",
  admin: "Admin",
};

const UserMenu = () => {
  const { user, profile, roles, signOut, isLoading, getFullName, openProfileSelector, switchProfile } = useAuth();
  const { isActingAsChild, parentUserId } = useActiveProfile();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
    );
  }

  if (!user) {
    return (
      <Button size="sm" onClick={() => navigate("/auth")}>
        Sign In
      </Button>
    );
  }

  const fullName = getFullName();
  const initials = fullName !== "User"
    ? fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.email?.slice(0, 2).toUpperCase() || "U";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
          <Avatar className="h-10 w-10 border-2 border-border">
            <AvatarImage src={profile?.avatar_url || undefined} alt={fullName} />
            <AvatarFallback className="bg-primary/10 text-primary font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-2">
            <p className="text-sm font-medium leading-none">
              {fullName}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
            {roles.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {roles.map((role) => {
                  const Icon = roleIcons[role];
                  return (
                    <Badge key={role} variant="secondary" className="text-xs gap-1">
                      <Icon className="w-3 h-3" />
                      {roleLabels[role]}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isActingAsChild && parentUserId && (
          <DropdownMenuItem 
            onClick={() => switchProfile(parentUserId)} 
            className="cursor-pointer"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Parent View
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={openProfileSelector} className="cursor-pointer">
          <RefreshCw className="mr-2 h-4 w-4" />
          Switch Profile
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
